// src/components/home/WorkspacePriorities.tsx
//
// "Workspace Priorities" — an ADDITIVE, read-only section that renders the server
// Execution Engine's Priorities projection ("what is most important for the
// workspace"). It coexists with the client-side "Today's Focus" ("what should I
// work on today"); the two intentionally answer different questions and neither
// replaces the other.
//
// Fully feature-gated and reversible:
//   • frontend runtime flag `crm.execution_engine.workspace_priorities` (fail-closed)
//   • backend `EXECUTION_ENGINE_ENABLED` (404 when off → section renders nothing)
// It performs no mutations, no navigation-to-mutation, and changes no engine code.

import { useState } from 'react';
import { ChevronDown, RefreshCw } from 'lucide-react';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useGetExecutionStateQuery } from '../../api/executionStateApi';
import type { PriorityItemDTO } from '../../types/executionState';

const TIER: Record<number, { label: string; cls: string }> = {
  1: { label: 'ROI Emergency', cls: 'text-red-700 bg-red-100' },
  2: { label: 'Client Emergency', cls: 'text-red-700 bg-red-100' },
  3: { label: 'Client Critical', cls: 'text-orange-700 bg-orange-100' },
  4: { label: 'ROI Critical', cls: 'text-orange-700 bg-orange-100' },
  5: { label: 'Client Due Tomorrow', cls: 'text-amber-700 bg-amber-100' },
  6: { label: 'ROI Internal', cls: 'text-gray-700 bg-gray-100' },
  7: { label: 'Maintenance', cls: 'text-gray-600 bg-gray-100' },
};

function timeAgo(iso: string): string {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function PriorityRow({ rank, item }: { rank: number; item: PriorityItemDTO }) {
  const tier = TIER[item.severityTier] ?? TIER[6];
  const clients = item.impact?.affectedClients ?? [];
  const consequence = item.impact?.consequenceIfDelayed;
  return (
    <li className="flex items-start gap-3 py-2 border-t border-gray-50 first:border-t-0">
      <span className="text-xs font-semibold text-gray-400 w-4 mt-0.5">{rank}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
          <span className={`flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${tier.cls}`}>
            {tier.label}
          </span>
        </div>
        {item.rationale?.reason && (
          <p className="text-xs text-gray-600 mt-0.5">{item.rationale.reason}</p>
        )}
        {(clients.length > 0 || consequence) && (
          <p className="text-[11px] text-gray-400 mt-0.5">
            {clients.length > 0 && (
              <>Affects: {clients.map((c) => c.label ?? c.id).join(', ')} · </>
            )}
            {consequence}
          </p>
        )}
      </div>
    </li>
  );
}

export default function WorkspacePriorities({ workspaceId }: { workspaceId: string }) {
  const [open, setOpen] = useState(true);
  const enabled = useFeatureFlag('crm.execution_engine.workspace_priorities');

  const { data, isLoading, isFetching, error, refetch } = useGetExecutionStateQuery(undefined, {
    skip: !enabled || !workspaceId,
    // One GET on initial load (and on remount). No focus/reconnect/poll refetch —
    // the engine is compute-on-read, so refreshes happen ONLY via the Refresh button.
    refetchOnMountOrArgChange: true,
  });

  // Fail-closed on the frontend flag, and hide entirely when the backend engine is
  // off (404). Either gate off ⇒ the section does not exist for the user.
  if (!enabled) return null;
  if (error && 'status' in error && error.status === 404) return null;

  const priorities = data?.state.currentPriorities ?? [];
  const meta = data?.meta;
  const state = data?.state;

  return (
    <div className="border-t border-gray-100 pt-3" data-testid="workspace-priorities">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-left"
      >
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900">Workspace Priorities</h3>
            {priorities.length > 0 && (
              <span className="text-xs text-gray-400">({priorities.length})</span>
            )}
          </div>
          <p className="text-[11px] text-gray-400">Powered by the Execution Engine</p>
        </div>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="mt-2">
          <div className="flex items-center justify-between text-[11px] text-gray-400 mb-2">
            <span>
              {meta && state
                ? `Updated ${timeAgo(meta.computedAt)} · confidence ${Math.round((state.currentConfidence ?? 0) * 100)}% · ${state.coverage.implementedSlices}/${state.coverage.totalSlices} views`
                : ''}
            </span>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="flex items-center gap-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
              aria-label="Refresh workspace priorities"
            >
              <RefreshCw className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {isLoading ? (
            <div className="space-y-2" aria-hidden>
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-8 rounded bg-gray-50 animate-pulse" />
              ))}
            </div>
          ) : error && !data ? (
            // Initial load failed with no prior snapshot to show.
            <p className="text-xs text-gray-500">
              Priorities are temporarily unavailable.{' '}
              <button onClick={() => refetch()} className="text-blue-600 hover:underline">
                Retry
              </button>
            </p>
          ) : priorities.length === 0 ? (
            <p className="text-xs text-gray-500">No workspace priorities right now.</p>
          ) : (
            <>
              {error && (
                // A refresh failed but a prior snapshot exists — keep showing it.
                <p className="text-[11px] text-amber-600 mb-1">
                  Unable to refresh. Showing the most recent execution results.
                </p>
              )}
              <ol className="space-y-0">
                {priorities.map((item, idx) => (
                  <PriorityRow key={item.ref.id} rank={idx + 1} item={item} />
                ))}
              </ol>
            </>
          )}
        </div>
      )}
    </div>
  );
}
